import {
  framingOptionLabel,
  type FramingQuestionId,
  type FramingResolution,
} from "~/utils/demandFraming"
import { adaptationLabels } from "~/components/demand-2/shared/preorderDevelopment"
import type { CollectionKind } from "~/utils/collectionKind"
import { DEFAULT_FASHION_PREORDER_MODE } from "~/utils/demandFashionPreorderDevelopment"
import { adaptationSchema } from "~/utils/demandPreorderDevelopment"

type AnswerLabelContext = {
  /** The collection's kind: `preorderDevelopment` words `closeBook` per kind. Null reads as fashion. */
  kind?: CollectionKind | null
  /** `"node"` where answered on a hierarchy node, for options that read differently there. */
  scope?: "node"
}

/**
 * A resolved answer's badge label, in its picker's wording; null when nothing honest to quote.
 * `preorderDevelopment` reads the kind's labels (`adaptationLabels`) and one default
 * (`DEFAULT_FASHION_PREORDER_MODE`) for both kinds.
 */
export const insightAnswerLabel = (
  question: FramingQuestionId,
  resolution: FramingResolution,
  { kind = null, scope }: AnswerLabelContext = {},
): string | null => {
  if (question === "preorderDevelopment") {
    const labels = adaptationLabels(kind)
    if (resolution.isDefault) return labels[DEFAULT_FASHION_PREORDER_MODE]
    const mode = adaptationSchema.safeParse(resolution.answer)
    return mode.success ? labels[mode.data] : null
  }
  return framingOptionLabel(question, resolution.answer, scope)
}

/** A stored answer's label. A season quotes its default; NOOS reads a cleared answer as cleared. */
export const kindAnswerLabel = (
  kind: CollectionKind | null,
  question: FramingQuestionId,
  answer: string | null | undefined,
  isDefault: boolean,
  scope?: "node",
): string | null =>
  kind !== "fashion" && answer == null
    ? null
    : insightAnswerLabel(
        question,
        { answer: answer ?? undefined, inherited: false, isDefault },
        { kind, scope },
      )
